import React, { useState, useEffect } from 'react';
import { 
  Wallet,
  Users,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  Minus,
  Download,
  Upload,
  Search,
  Filter,
  BarChart3,
  TrendingUp,
  DollarSign,
  Clock,
  FileText,
  RefreshCw,
  Settings,
  Activity,
  Lock,
  Unlock,
  RotateCcw
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const EnhancedWalletManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for different tabs
  const [wallets, setWallets] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [bulkOperations, setBulkOperations] = useState([]);
  const [financialReports, setFinancialReports] = useState([]);
  
  // UI state
  const [selectedWallets, setSelectedWallets] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Forms
  const [bulkForm, setBulkForm] = useState({
    operation_type: 'balance_adjustment',
    parameters: {}
  });
  const [disputeForm, setDisputeForm] = useState({
    user_id: '',
    transaction_id: '',
    dispute_type: 'incorrect_amount',
    amount_cents: '',
    description: ''
  });
  const [reportForm, setReportForm] = useState({
    period_start: '',
    period_end: '',
    jurisdiction: 'US'
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'overview') {
        await fetchWallets();
      } else if (activeTab === 'fraud') {
        await fetchFraudAlerts();
      } else if (activeTab === 'disputes') {
        await fetchDisputes();
      } else if (activeTab === 'bulk') {
        await fetchBulkOperations();
      } else if (activeTab === 'reports') {
        await fetchFinancialReports();
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWallets = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/wallets`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setWallets(data.wallets || []);
    } else {
      throw new Error('Failed to fetch wallets');
    }
  };

  const fetchFraudAlerts = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/wallets/fraud-alerts?days=30`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setFraudAlerts(data.alerts || []);
    } else {
      throw new Error('Failed to fetch fraud alerts');
    }
  };

  const fetchDisputes = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/wallets/disputes`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setDisputes(data.disputes || []);
    } else {
      throw new Error('Failed to fetch disputes');
    }
  };

  const fetchBulkOperations = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/wallets/bulk-operations`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setBulkOperations(data.operations || []);
    } else {
      throw new Error('Failed to fetch bulk operations');
    }
  };

  const fetchFinancialReports = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/wallets/reports`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setFinancialReports(data.reports || []);
    } else {
      throw new Error('Failed to fetch financial reports');
    }
  };

  const handleBulkOperation = async (e) => {
    e.preventDefault();
    
    if (selectedWallets.size === 0) {
      setError('Please select wallets for bulk operation');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/wallets/bulk-operation`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          operation_name: `Bulk ${bulkForm.operation_type} ${new Date().toISOString().slice(0, 16)}`,
          operation_type: bulkForm.operation_type,
          target_wallets: Array.from(selectedWallets),
          parameters: bulkForm.parameters
        })
      });

      if (response.ok) {
        setShowBulkModal(false);
        setSelectedWallets(new Set());
        setBulkForm({ operation_type: 'balance_adjustment', parameters: {} });
        if (activeTab === 'bulk') {
          fetchBulkOperations();
        }
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to execute bulk operation');
      }
    } catch (err) {
      setError('Failed to execute bulk operation');
    }
  };

  const handleCreateDispute = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/wallets/disputes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...disputeForm,
          amount_cents: parseInt(disputeForm.amount_cents)
        })
      });

      if (response.ok) {
        setShowDisputeModal(false);
        setDisputeForm({
          user_id: '',
          transaction_id: '',
          dispute_type: 'incorrect_amount',
          amount_cents: '',
          description: ''
        });
        if (activeTab === 'disputes') {
          fetchDisputes();
        }
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to create dispute');
      }
    } catch (err) {
      setError('Failed to create dispute');
    }
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/wallets/reports/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportForm)
      });

      if (response.ok) {
        setShowReportModal(false);
        setReportForm({
          period_start: '',
          period_end: '',
          jurisdiction: 'US'
        });
        if (activeTab === 'reports') {
          fetchFinancialReports();
        }
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to generate report');
      }
    } catch (err) {
      setError('Failed to generate report');
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Wallet Overview</h3>
          <p className="text-gray-600 dark:text-gray-300">Comprehensive wallet management and monitoring</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowBulkModal(true)}
            disabled={selectedWallets.size === 0}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Settings className="w-4 h-4 mr-2 inline" />
            Bulk Actions
          </button>
        </div>
      </div>

      {/* Wallet Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Wallets', value: wallets.length, icon: Wallet, color: 'text-blue-600' },
          { label: 'Active Wallets', value: wallets.filter(w => w.status === 'active').length, icon: CheckCircle, color: 'text-green-600' },
          { label: 'Blocked Wallets', value: wallets.filter(w => w.status === 'blocked').length, icon: Lock, color: 'text-red-600' },
          { label: 'Total Balance', value: `$${(wallets.reduce((sum, w) => sum + (w.balance_cents || 0), 0) / 100).toFixed(2)}`, icon: DollarSign, color: 'text-purple-600' }
        ].map((stat, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center">
              <stat.icon className={`w-5 h-5 ${stat.color} mr-3`} />
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search wallets by email or ID..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
        </div>
        <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700">
          <Filter className="w-4 h-4 mr-2 inline" />
          Filter
        </button>
      </div>

      {/* Wallets Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedWallets(new Set(wallets.map(w => w.id)));
                    } else {
                      setSelectedWallets(new Set());
                    }
                  }}
                  className="rounded border-gray-300"
                />
              </TableHead>
              <TableHead>User</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Risk Score</TableHead>
              <TableHead>Last Activity</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {wallets
              .filter(wallet => 
                !searchTerm || 
                wallet.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                wallet.id.includes(searchTerm)
              )
              .map(wallet => (
                <TableRow key={wallet.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedWallets.has(wallet.id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedWallets);
                        if (e.target.checked) {
                          newSelected.add(wallet.id);
                        } else {
                          newSelected.delete(wallet.id);
                        }
                        setSelectedWallets(newSelected);
                      }}
                      className="rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{wallet.user_email || 'Unknown'}</div>
                      <div className="text-sm text-gray-500">{wallet.user_id?.slice(0, 8)}...</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">${(wallet.balance_cents / 100).toFixed(2)}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      wallet.status === 'active' ? 'success' :
                      wallet.status === 'blocked' ? 'error' :
                      wallet.status === 'frozen' ? 'warning' : 'secondary'
                    }>
                      {wallet.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {wallet.risk_score > 70 ? (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      ) : wallet.risk_score > 40 ? (
                        <Clock className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      <span className={
                        wallet.risk_score > 70 ? 'text-red-600 font-medium' :
                        wallet.risk_score > 40 ? 'text-yellow-600' : 'text-green-600'
                      }>
                        {wallet.risk_score || 0}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {wallet.last_transaction ? 
                      new Date(wallet.last_transaction).toLocaleDateString() :
                      'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <button className="p-1 text-gray-500 hover:text-blue-600">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-gray-500 hover:text-green-600">
                        <Plus className="w-4 h-4" />
                      </button>
                      {wallet.status === 'active' ? (
                        <button className="p-1 text-gray-500 hover:text-red-600">
                          <Lock className="w-4 h-4" />
                        </button>
                      ) : (
                        <button className="p-1 text-gray-500 hover:text-green-600">
                          <Unlock className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const FraudTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Fraud Detection</h3>
          <p className="text-gray-600 dark:text-gray-300">Monitor and review fraud alerts</p>
        </div>
      </div>

      {/* Fraud Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Open Alerts', value: fraudAlerts.filter(a => a.status === 'open').length, icon: AlertTriangle, color: 'text-red-600' },
          { label: 'High Risk', value: fraudAlerts.filter(a => a.risk_score > 70).length, icon: Shield, color: 'text-orange-600' },
          { label: 'Resolved', value: fraudAlerts.filter(a => a.status === 'resolved').length, icon: CheckCircle, color: 'text-green-600' },
          { label: 'Avg Risk Score', value: fraudAlerts.length > 0 ? (fraudAlerts.reduce((sum, a) => sum + a.risk_score, 0) / fraudAlerts.length).toFixed(1) : '0', icon: BarChart3, color: 'text-blue-600' }
        ].map((stat, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center">
              <stat.icon className={`w-5 h-5 ${stat.color} mr-3`} />
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fraud Alerts Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Alert Type</TableHead>
              <TableHead>Risk Score</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fraudAlerts.map(alert => (
              <TableRow key={alert.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{alert.user_email}</div>
                    <div className="text-sm text-gray-500">{alert.user_id?.slice(0, 8)}...</div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="warning">{alert.alert_type}</Badge>
                </TableCell>
                <TableCell>
                  <div className={`font-medium ${
                    alert.risk_score > 70 ? 'text-red-600' :
                    alert.risk_score > 40 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {alert.risk_score.toFixed(1)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-600">
                    {alert.details?.triggered_rules?.map(rule => rule.rule_name).join(', ') || 'Anomaly detected'}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={
                    alert.status === 'open' ? 'warning' :
                    alert.status === 'resolved' ? 'success' : 'secondary'
                  }>
                    {alert.status}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(alert.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  {alert.status === 'open' && (
                    <div className="flex gap-2">
                      <button className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">
                        Approve
                      </button>
                      <button className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">
                        Block
                      </button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const DisputesTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Transaction Disputes</h3>
          <p className="text-gray-600 dark:text-gray-300">Manage internal transaction disputes</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowDisputeModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Create Dispute
          </button>
        </div>
      </div>

      {/* Disputes Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispute #</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {disputes.map(dispute => (
              <TableRow key={dispute.id}>
                <TableCell>
                  <span className="font-mono text-sm">{dispute.dispute_number}</span>
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{dispute.user_email}</div>
                    <div className="text-sm text-gray-500">{dispute.user_id?.slice(0, 8)}...</div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="info">{dispute.dispute_type}</Badge>
                </TableCell>
                <TableCell>
                  <div className="font-medium">${(dispute.amount_cents / 100).toFixed(2)}</div>
                </TableCell>
                <TableCell>
                  <Badge variant={
                    dispute.status === 'resolved' ? 'success' :
                    dispute.status === 'rejected' ? 'error' :
                    dispute.status === 'investigating' ? 'warning' : 'secondary'
                  }>
                    {dispute.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={
                    dispute.priority === 'urgent' ? 'error' :
                    dispute.priority === 'high' ? 'warning' : 'secondary'
                  }>
                    {dispute.priority}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(dispute.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button className="p-1 text-gray-500 hover:text-blue-600">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const BulkOperationsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Bulk Operations</h3>
          <p className="text-gray-600 dark:text-gray-300">Track bulk wallet operations</p>
        </div>
      </div>

      {/* Bulk Operations Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Operation</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Targets</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Success Rate</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bulkOperations.map(operation => (
              <TableRow key={operation.id}>
                <TableCell>
                  <div className="font-medium">{operation.operation_name}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="info">{operation.operation_type}</Badge>
                </TableCell>
                <TableCell>{operation.total_targets}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${operation.progress_percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">
                      {operation.progress_percentage.toFixed(1)}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {operation.processed_count > 0 ? (
                    <div className="text-sm">
                      <div className="text-green-600">{operation.success_count}/{operation.processed_count}</div>
                    </div>
                  ) : (
                    <span className="text-gray-500">Not started</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={
                    operation.status === 'completed' ? 'success' :
                    operation.status === 'failed' ? 'error' :
                    operation.status === 'running' ? 'warning' : 'secondary'
                  }>
                    {operation.status}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(operation.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button className="p-1 text-gray-500 hover:text-blue-600">
                      <Eye className="w-4 h-4" />
                    </button>
                    {operation.status === 'completed' && operation.error_count > 0 && (
                      <button className="p-1 text-gray-500 hover:text-yellow-600">
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const ReportsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Financial Reports</h3>
          <p className="text-gray-600 dark:text-gray-300">Tax-ready financial reporting and exports</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowReportModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            <FileText className="w-4 h-4 mr-2 inline" />
            Generate Report
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Report Type</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Jurisdiction</TableHead>
              <TableHead>Volume</TableHead>
              <TableHead>Tax Liability</TableHead>
              <TableHead>Generated</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {financialReports.map(report => (
              <TableRow key={report.id}>
                <TableCell>
                  <Badge variant="info">{report.report_type}</Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{new Date(report.period_start).toLocaleDateString()}</div>
                    <div className="text-gray-500">to {new Date(report.period_end).toLocaleDateString()}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{report.jurisdiction}</Badge>
                </TableCell>
                <TableCell>
                  <div className="font-medium">${(report.total_volume_cents / 100).toFixed(2)}</div>
                  <div className="text-sm text-gray-500">{report.total_transactions} transactions</div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">${(report.tax_liability_cents / 100).toFixed(2)}</div>
                </TableCell>
                <TableCell>{new Date(report.generated_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button className="p-1 text-gray-500 hover:text-blue-600">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // Bulk Operation Modal
  const BulkModal = () => (
    showBulkModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Bulk Wallet Operation
          </h3>
          
          <form onSubmit={handleBulkOperation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Operation Type
              </label>
              <select
                value={bulkForm.operation_type}
                onChange={(e) => setBulkForm({...bulkForm, operation_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="balance_adjustment">Balance Adjustment</option>
                <option value="status_change">Status Change</option>
                <option value="transaction_reversal">Transaction Reversal</option>
              </select>
            </div>
            
            {bulkForm.operation_type === 'balance_adjustment' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  onChange={(e) => setBulkForm({
                    ...bulkForm, 
                    parameters: {...bulkForm.parameters, amount_cents: parseFloat(e.target.value) * 100}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  placeholder="10.00"
                  required
                />
              </div>
            )}
            
            {bulkForm.operation_type === 'status_change' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Status
                </label>
                <select
                  onChange={(e) => setBulkForm({
                    ...bulkForm, 
                    parameters: {...bulkForm.parameters, status: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  required
                >
                  <option value="">Select status...</option>
                  <option value="active">Active</option>
                  <option value="frozen">Frozen</option>
                  <option value="blocked">Blocked</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason
              </label>
              <textarea
                onChange={(e) => setBulkForm({
                  ...bulkForm, 
                  parameters: {...bulkForm.parameters, reason: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                rows={3}
                required
                placeholder="Reason for bulk operation..."
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 dark:bg-yellow-900/10 dark:border-yellow-800">
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                This will affect {selectedWallets.size} wallet{selectedWallets.size !== 1 ? 's' : ''}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                Execute Bulk Operation
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading wallet management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'overview', label: 'Overview', icon: Wallet },
            { key: 'fraud', label: 'Fraud Detection', icon: Shield },
            { key: 'disputes', label: 'Disputes', icon: AlertTriangle },
            { key: 'bulk', label: 'Bulk Operations', icon: Settings },
            { key: 'reports', label: 'Financial Reports', icon: FileText }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'fraud' && <FraudTab />}
      {activeTab === 'disputes' && <DisputesTab />}
      {activeTab === 'bulk' && <BulkOperationsTab />}
      {activeTab === 'reports' && <ReportsTab />}

      {/* Modals */}
      <BulkModal />
    </div>
  );
};

export default EnhancedWalletManagement;